/**
 * The pictures of "How to play" (U-LEARN-02): a few cells and ships set up on the engine and
 * drawn by the board itself, so they never fall behind the artwork. Each one is a fixed
 * position plus the marks the game screen would show over it.
 */
import {
  parseCellId,
  toCellId,
  type MapDef,
  type PlacedShip,
  type Ruleset,
  type ShipClassId,
} from '@brig/rules-engine';
import { buildHistory, type History } from '../game/history.ts';
import type { Topic } from './topics.ts';

export interface Picture {
  readonly history: History;
  readonly selected?: string;
  readonly draftGroup?: readonly string[];
  readonly movable?: readonly string[];
  readonly moveCells?: readonly string[];
  /** Cells the wind would allow but land or a reef blocks (U-MOVE-02). */
  readonly blockedCells?: readonly string[];
  readonly targets?: readonly string[];
  readonly volleyShips?: readonly string[];
  readonly volleyTarget?: string;
  readonly highlight?: readonly string[];
}

/** N, NE, E, SE, S, SW, W, NW: rows grow southwards (R-MAP-02). */
const RAYS = [
  [0, -1],
  [1, -1],
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
] as const;
const EAST = 2;

/**
 * Cells along each ray up to `reach(ray)` steps, stopping at land, reefs and the edge
 * (R-MOVE-01/05); the rest of the ray within reach is `blocked`, as the game draws it.
 */
function rayCells(map: MapDef, from: string, reach: (ray: number) => number) {
  const start = parseCellId(from);
  const cells: string[] = [];
  const blocked: string[] = [];
  if (!start) return { cells, blocked };
  RAYS.forEach(([dx, dy], ray) => {
    let open = true;
    for (let step = 1; step <= reach(ray); step++) {
      const x = start.x + dx * step;
      const y = start.y + dy * step;
      const ch = map.rows[y]?.[x];
      if (ch === undefined) break;
      if (ch === '#' || ch === 'F' || ch === '*') open = false;
      (open ? cells : blocked).push(toCellId({ x, y }));
    }
  });
  return { cells, blocked };
}

/** R-MOVE-02/03: fresh wind, `k` steps of 45° off downwind; powered ships keep their calm speed. */
function freshReach(ruleset: Ruleset, cls: ShipClassId, downwind: number) {
  const c = ruleset.shipClasses[cls];
  return (ray: number) => {
    const k = Math.min(Math.abs(ray - downwind), 8 - Math.abs(ray - downwind));
    const n = Math.max(0, c.freshSpeed - k);
    return c.propulsion !== 'sail' && ruleset.oarsAndSteamUseBest ? Math.max(n, c.calmSpeed) : n;
  };
}

function open(rows: readonly string[]): MapDef {
  return {
    id: 'lesson',
    name: 'lesson',
    width: rows[0]?.length ?? 0,
    height: rows.length,
    rows,
    forts: [],
    royalPorts: {},
  };
}

/** Two harbours facing each other, each side's Royal Port below its own. */
function harbours(): MapDef {
  return {
    ...open(['#F#......#F#', 'pp,......,pp', '............', 'R..........R', '............', '............']),
    forts: [
      { id: 'rf', side: 'red', class: 'first_line', cell: 'B1', portCells: ['A2', 'B2'] },
      { id: 'bf', side: 'blue', class: 'first_line', cell: 'K1', portCells: ['K2', 'L2'] },
    ],
    royalPorts: { red: 'A4', blue: 'L4' },
  };
}

function lesson(
  ruleset: Ruleset,
  map: MapDef,
  ships: readonly PlacedShip[],
  fortHits?: Record<string, number>,
): History {
  return buildHistory({
    map,
    ruleset: { ...ruleset, firstSide: 'red' },
    seed: 1,
    players: { red: { id: 'r', name: 'Red' }, blue: { id: 'b', name: 'Blue' } },
    options: { ships, ...(fortHits ? { fortHits } : {}) },
  });
}

const FLEETS: readonly PlacedShip[] = [
  { id: 'r1', side: 'red', cls: 'ship_of_line', cell: 'B2' },
  { id: 'r2', side: 'red', cls: 'brig', cell: 'D3' },
  { id: 'r3', side: 'red', cls: 'steam_frigate', cell: 'C5' },
  { id: 'b1', side: 'blue', cls: 'galleon', cell: 'K2' },
  { id: 'b2', side: 'blue', cls: 'sail_frigate', cell: 'I3' },
  { id: 'b3', side: 'blue', cls: 'galley', cell: 'J5' },
];

export function picture(topic: Topic, ruleset: Ruleset): Picture {
  switch (topic) {
    case 'goal':
    case 'end':
      return { history: lesson(ruleset, harbours(), FLEETS), highlight: ['A4', 'L4'] };
    case 'turn':
      return { history: lesson(ruleset, harbours(), FLEETS), movable: ['r2', 'r3'], targets: ['b2'] };
    case 'muster':
      return { history: lesson(ruleset, harbours(), FLEETS), draftGroup: ['r2', 'r3'] };
    case 'wind': {
      const map = open(['.........', '.........', '......#..', '.........', '.........']);
      const reach = rayCells(map, 'C3', freshReach(ruleset, 'brig', EAST));
      return {
        history: lesson(ruleset, map, [
          { id: 'r', side: 'red', cls: 'brig', cell: 'C3' },
          { id: 'b', side: 'blue', cls: 'galleon', cell: 'I1' },
        ]),
        selected: 'r',
        moveCells: reach.cells,
        blockedCells: reach.blocked,
      };
    }
    case 'movement': {
      const map = open(['.........', '..#......', '.........', '.........', '.........']);
      const calm = ruleset.shipClasses.galley.calmSpeed;
      const reach = rayCells(map, 'D3', () => calm);
      return {
        history: lesson(ruleset, map, [
          { id: 'g', side: 'red', cls: 'galley', cell: 'D3' },
          { id: 's', side: 'red', cls: 'ship_of_line', cell: 'G4' },
        ]),
        selected: 'g',
        movable: ['g'],
        moveCells: reach.cells,
        blockedCells: reach.blocked,
      };
    }
    case 'storm': {
      const map = open(['.........', '......*..', '.........', '.........', '.........']);
      const drift = (cls: ShipClassId, from: string) =>
        rayCells(map, from, (ray) => (ray === EAST ? ruleset.shipClasses[cls].stormDrift : 0)).cells.concat(
          // The reef itself: where the galleon is driven and wrecked (R-STORM-03).
          cls === 'galleon' ? ['G2'] : [],
        );
      return {
        history: lesson(ruleset, map, [
          { id: 'g', side: 'red', cls: 'galleon', cell: 'D2' },
          { id: 'b', side: 'red', cls: 'brig', cell: 'D4' },
        ]),
        draftGroup: ['g', 'b'],
        highlight: [...drift('galleon', 'D2'), ...drift('brig', 'D4')],
      };
    }
    case 'fire': {
      const map = open(['.........', '...#.....', '.........', '.........', '.........']);
      return {
        history: lesson(ruleset, map, [
          { id: 'a', side: 'red', cls: 'first_rate', cell: 'C3' },
          { id: 'c', side: 'red', cls: 'brig', cell: 'F5' },
          { id: 't', side: 'blue', cls: 'galleon', cell: 'F3' },
          // Behind the island: on a ray, in range, and still out of the line of fire (R-FIRE-02).
          { id: 'h', side: 'blue', cls: 'brig', cell: 'E1' },
        ]),
        targets: ['t'],
        volleyShips: ['a', 'c'],
        volleyTarget: 't',
      };
    }
    case 'ports':
      return {
        history: lesson(ruleset, harbours(), [
          { id: 'd', side: 'red', cls: 'ship_of_line', cell: 'A2', damage: 2 },
          { id: 'p', side: 'red', cls: 'brig', cell: 'B2' },
          { id: 'e', side: 'blue', cls: 'galley', cell: 'E3' },
        ]),
        selected: 'd',
        highlight: ['A2', 'B2'],
      };
    case 'forts':
      return {
        history: lesson(
          ruleset,
          harbours(),
          [
            { id: 'l', side: 'red', cls: 'ship_of_line', cell: 'K4' },
            { id: 'k', side: 'red', cls: 'brig', cell: 'H4' },
            { id: 'g', side: 'blue', cls: 'galleon', cell: 'L2' },
          ],
          { bf: 3 },
        ),
        targets: ['bf'],
        volleyShips: ['l', 'k'],
        volleyTarget: 'bf',
      };
    case 'royal':
      return {
        history: lesson(ruleset, harbours(), [
          { id: 'r', side: 'red', cls: 'brig', cell: 'L4' },
          { id: 's', side: 'red', cls: 'sail_frigate', cell: 'H5' },
          { id: 'b', side: 'blue', cls: 'galley', cell: 'I3' },
        ]),
        selected: 'r',
        highlight: ['L4'],
      };
  }
}
